import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import OneVerseLogo from '../../../components/OneVerseLogo';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

interface ConfirmEmailFormProps {
  email: string;
  onSubmit: (code: string, email: string) => Promise<void>;
  onResend: (email: string) => Promise<void>;
  resendCooldown: number;
  error?: string | null;
  isLoading?: boolean;
  onClearError?: () => void;
  // When true, the email is shown as an editable field (standalone resume
  // flow); otherwise it is fixed and shown only in the instructions text.
  editableEmail?: boolean;
}

const ConfirmEmailForm: React.FC<ConfirmEmailFormProps> = ({
  email,
  onSubmit,
  onResend,
  resendCooldown,
  error,
  isLoading = false,
  onClearError,
  editableEmail = false,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [emailValue, setEmailValue] = useState(email);
  const [localError, setLocalError] = useState('');

  const effectiveEmail = editableEmail ? emailValue.trim() : email;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    if (editableEmail && !effectiveEmail) {
      setLocalError(t('auth.errors.email_required'));
      return;
    }

    if (!code.trim()) {
      setLocalError(t('auth.confirm_email.code_required'));
      return;
    }

    await onSubmit(code.trim(), effectiveEmail);
  };

  return (
    <div>
      <OneVerseLogo className="mb-6 mt-2 mx-auto" />
      <h1 className="text-center text-2xl font-semibold text-gray-900">
        {t('auth.confirm_email.title')}
      </h1>
      <p className="text-center text-gray-500 mb-6">
        {editableEmail
          ? t('auth.confirm_email.instructions_standalone')
          : t('auth.confirm_email.instructions', { email })}
      </p>

      <div className="w-full max-w-md mx-auto p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {editableEmail && (
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-medium">
                {t('auth.email')}
              </Label>
              <Input
                id="email"
                type="email"
                value={emailValue}
                onChange={e => {
                  setEmailValue(e.target.value);
                  setLocalError('');
                  onClearError?.();
                }}
                className="bg-input border-0 rounded-lg h-12 px-4"
                placeholder={t('auth.email_placeholder')}
                disabled={isLoading}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label
              htmlFor="confirmationCode"
              className="text-gray-700 font-medium"
            >
              {t('auth.confirmation_code')}
            </Label>
            <Input
              id="confirmationCode"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={e => {
                setCode(e.target.value);
                setLocalError('');
                onClearError?.();
              }}
              className="bg-input border-0 rounded-lg h-12 px-4"
              placeholder={t('auth.confirm_email.code_placeholder')}
              disabled={isLoading}
            />
          </div>

          {(localError || error) && (
            <p className="text-red-500 text-center">{localError || error}</p>
          )}

          <Button
            type="submit"
            className="w-full h-12 bg-primary hover:bg-primary-600 text-primary-foreground font-semibold rounded-lg text-base"
            disabled={isLoading}
          >
            {isLoading
              ? t('auth.confirm_email.verifying')
              : t('auth.confirm_email.verify')}
          </Button>

          <Button
            type="button"
            onClick={() => onResend(effectiveEmail)}
            variant="outline"
            className="w-full h-12 border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg text-base"
            disabled={
              isLoading ||
              resendCooldown > 0 ||
              (editableEmail && !effectiveEmail)
            }
          >
            {resendCooldown > 0
              ? t('auth.confirm_email.resend_in', { seconds: resendCooldown })
              : t('auth.confirm_email.resend')}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ConfirmEmailForm;
